import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Discover from "@/pages/Discover";
import Circles from "@/pages/Circles";
import CircleForum from "@/pages/CircleForum";
import Content from "@/pages/Content";
import Events from "@/pages/Events";
import Experts from "@/pages/Experts";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import Settings from "@/pages/Settings";
import Messages from "@/pages/Messages";
import PrivateChat from "@/pages/PrivateChat";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src="https://customer-assets.emergentagent.com/job_68779cce-c06c-4c5e-9076-4d6253f6c7a5/artifacts/j5uq6m0s_Artboard%204_4%403x.png" 
            alt="WOMBS" 
            className="h-16 opacity-50"
          />
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Redirect to onboarding if profile not completed
  if (!user.profile_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src="https://customer-assets.emergentagent.com/job_68779cce-c06c-4c5e-9076-4d6253f6c7a5/artifacts/j5uq6m0s_Artboard%204_4%403x.png" 
            alt="WOMBS" 
            className="h-16 opacity-50"
          />
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return null;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id during render (NOT in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/discover" element={
        <ProtectedRoute>
          <Discover />
        </ProtectedRoute>
      } />
      <Route path="/circles" element={
        <ProtectedRoute>
          <Circles />
        </ProtectedRoute>
      } />
      <Route path="/circles/:circleId" element={
        <ProtectedRoute>
          <CircleForum />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/chat/:connectionId" element={
        <ProtectedRoute>
          <PrivateChat />
        </ProtectedRoute>
      } />
      <Route path="/content" element={
        <ProtectedRoute>
          <Content />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <Events />
        </ProtectedRoute>
      } />
      <Route path="/experts" element={
        <ProtectedRoute>
          <Experts />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/profile/edit" element={
        <ProtectedRoute>
          <EditProfile />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      {/* Public Legal Pages */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authCheckDone = useRef(false);

  const checkAuth = async () => {
    // Skip if we already have a user set (from login callback)
    if (user) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Only clear user if this is the initial auth check
      if (!authCheckDone.current) {
        setUser(null);
      }
    } finally {
      setLoading(false);
      authCheckDone.current = true;
    }
  };

  useEffect(() => {
    // Don't check auth if we're on the callback page (session_id in URL)
    const hasSessionInUrl = window.location.hash?.includes('session_id=');
    if (hasSessionInUrl) {
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (userData) => {
    console.log("Login called with user:", userData?.email);
    setUser(userData);
    setLoading(false);
    authCheckDone.current = true;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    authCheckDone.current = false;
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-charcoal">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
